import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'
import { useAdapter } from '@/hooks/useAdapter'
import { useAppView } from '@/hooks/useAppView'
import { accountQueryIdentifier } from '@/lib/accountQueryIdentifier'
import type { Platform } from '@/contexts/ProtocolContext/ProtocolContext'
import type { AppView } from '@/contexts/AppViewContext/AppViewContext'
import type { PostsPage } from '@/types/posts'

export const timelineQueryKey = (
    platform: Platform,
    identifier: string | undefined,
    feedUri?: string,
    appView?: AppView
) =>
    [
        'timeline',
        platform,
        identifier ?? null,
        feedUri ?? 'following',
        appView ?? 'bluesky',
    ] as const

export function useTimeline(feedUri?: string, { enabled = true }: { enabled?: boolean } = {}) {
    const { session, mastodonSession, pixelfedSession, nostrSession } = useAuth()
    const { selectedProtocol } = usePlatform()
    const { adapter, session: adapterSession } = useAdapter()
    const { publicAgent, appView, sessionAgent } = useAppView()

    // Derive a stable per-account identifier for the query key
    const identifier =
        accountQueryIdentifier(selectedProtocol, {
            session,
            mastodonSession,
            pixelfedSession,
            nostrSession,
        }) ?? undefined

    const queryKey = useMemo(
        () => timelineQueryKey(selectedProtocol, identifier, feedUri, appView),
        [selectedProtocol, identifier, feedUri, appView]
    )

    const query = useInfiniteQuery<PostsPage, Error>({
        queryKey,
        queryFn: async ({ pageParam }) => {
            if (!adapterSession) throw new Error('Session unavailable')
            return adapter.fetchTimeline(adapterSession, {
                cursor: pageParam as string | undefined,
                feedUri,
                publicAgent,
                sessionAgent,
            })
        },
        initialPageParam: undefined as string | undefined,
        getNextPageParam: lastPage => lastPage.cursor ?? undefined,
        enabled: enabled && !!adapterSession,
        staleTime: 60_000,
        // Only keep previous data when platform, account, and feedUri are all unchanged (e.g. an
        // appView switch on the same feed). Clearing on an account or feedUri change prevents the
        // previous account's posts (#2458) or the old tab's posts (bug #849) from flashing briefly
        // while the new query loads.
        placeholderData: (previousData, previousQuery) => {
            const prevPlatform = previousQuery?.queryKey?.[1]
            const prevIdentifier = previousQuery?.queryKey?.[2]
            const prevFeedUri = previousQuery?.queryKey?.[3]
            return prevPlatform === selectedProtocol &&
                prevIdentifier === (identifier ?? null) &&
                prevFeedUri === (feedUri ?? 'following')
                ? previousData
                : undefined
        },
    })

    const posts = useMemo(() => query.data?.pages.flatMap(page => page.posts) ?? [], [query.data])

    const newestPostAt = query.data?.pages[0]?.posts[0]?.createdAt

    return {
        posts,
        queryKey,
        newestPostAt,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage ?? false,
        fetchNextPage: query.fetchNextPage,
        pageCount: query.data?.pages.length ?? 0,
        dataUpdatedAt: query.dataUpdatedAt,
        error: query.error,
        refetch: query.refetch,
    }
}
